import { SECTIONS } from '../data/sections';
import { ROLES, PHASES } from '../data/roles';
import { getSectionProgress, getOverallProgress } from './progress';

// ─────────────────────────────────────────────────────────────────
// ROLE DETECTION  — strict, ordered, longest-match wins
// ─────────────────────────────────────────────────────────────────
const ROLE_PATTERNS = [
  // DEVOPS must come before DEV — longest/most-specific first
  { key: 'DEVOPS',  phrases: ['devops engineer','devops','dev ops','infrastructure engineer','sre','site reliability','cloud engineer','deployment engineer'] },
  { key: 'COO',     phrases: ['coo','chief operating officer','chief executive','c-level executive'] },
  { key: 'PM',      phrases: ['project manager','program manager','project management','delivery manager'] },
  { key: 'FC',      phrases: ['functional consultant','business analyst','solution consultant','ba consultant','fc consultant'] },
  { key: 'TESTER',  phrases: ['quality assurance engineer','qa engineer','test engineer','software tester','quality analyst','tester','qa analyst','quality engineer','qa'] },
  { key: 'DEV',     phrases: ['software engineer','software developer','frontend developer','backend developer','full stack developer','full-stack developer','programmer','coder','developer','dev'] },
  { key: 'PM',      phrases: ['as a pm','as pm','role pm','i am pm','i\'m pm'] },
];

/**
 * detectRole — strict boundary matching, longest phrase wins.
 * "i'm a devops" → DEVOPS  (not DEV)
 * "i'm a developer" → DEV
 */
function detectRole(input) {
  const lower = ' ' + input.toLowerCase().trim() + ' ';
  for (const { key, phrases } of ROLE_PATTERNS) {
    const sorted = [...phrases].sort((a, b) => b.length - a.length);
    for (const phrase of sorted) {
      if (phrase.length <= 3) {
        if (lower.includes(' ' + phrase + ' ')) return key;
      } else {
        const re = new RegExp('(^|\\s|\\W)' + escapeRe(phrase) + '(\\s|\\W|$)', 'i');
        if (re.test(lower)) return key;
      }
    }
  }
  return null;
}

function escapeRe(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// ─────────────────────────────────────────────────────────────────
// SECTION DETECTION — keyword scoring
// ─────────────────────────────────────────────────────────────────
const SECTION_KW = {
  '0.1': ['kickoff','kick off','project start','scope document','initiation','project kick','start project'],
  '0.2': ['team assignment','role definition','team setup','team roles','assign roles'],
  '0.3': ['project setup','tracking system','project creation','milestone setup','create project'],
  '1.1': ['requirements elicitation','gather requirements','requirements session','elicitation','collecting requirements','workshop requirements'],
  '1.2': ['requirements sign','brd sign','requirements approval','sign requirements','requirements document','sign off requirements'],
  '1.3': ['change request','scope change','requirement change','change management','cr process','change order'],
  '2.1': ['sdd','lld','design document','solution design','design preparation','brd document'],
  '2.2': ['design approval','design review','client design','design sign off','approve design'],
  '3.1': ['task assignment','task handoff','handoff','task description','acceptance criteria','assign task','task creation'],
  '3.2': ['git','github','pull request','branch','merge','pull latest','version control','code push','commit','repo','repository'],
  '3.3': ['code review','peer review','review code','code quality','reviewer','pull request review'],
  '3.4': ['sprint','standup','daily standup','sprint planning','sprint review','agile','scrum','velocity'],
  '4.1': ['internal testing','test cases','qa testing','bug reporting','test execution','run tests','test results'],
  '4.2': ['bug lifecycle','bug tracking','bug sla','defect tracking','severity','bug status','bug log','bug escalation'],
  '5.1': ['uat readiness','uat preparation','testing sheet','user manual','validation report','uat ready','before uat'],
  '5.2': ['uat session','uat facilitation','run uat','uat meeting','customer testing','facilitate uat','uat with client'],
  '5.3': ['uat defect','uat bug','defect management','uat issue','client raised','uat defects','uat finding'],
  '6.1': ['deployment','deploy','staging','production deployment','go live','release','smoke test','rollback','deploy to'],
  '6.2': ['go-live communication','go live notification','deployment notification','post deployment','client go live','notify client deployment'],
  '7.1': ['milestone','invoice','completion certificate','billing','payment','sign milestone','milestone sign'],
  '7.2': ['knowledge transfer','handover','documentation handover','project handover','knowledge share','transfer docs'],
  '7.3': ['retrospective','retro','lessons learned','project closure','project close','project end','close project'],
  'X.1': ['accountability','pm report','weekly report','mom','minutes of meeting','tracking tasks','log requests','daily review','task log'],
  'X.2': ['client communication','email client','respond client','communication standard','contact client','client email','reply client'],
  'X.3': ['document versioning','versioning','document storage','file storage','v0.1','version number','store document','central storage'],
};

function detectSection(input) {
  const lower = input.toLowerCase();
  // Direct code e.g. "3.2" "X.3" "section 1.1"
  const codeMatch = lower.match(/\b([0-7]\.\d+|x\.\d+)\b/i);
  if (codeMatch) {
    const raw = codeMatch[1];
    const key = raw.toLowerCase().startsWith('x') ? 'X.' + raw.split('.')[1] : raw;
    if (SECTIONS[key]) return key;
  }
  // Keyword scoring
  let best = null, bestScore = 0;
  for (const [sec, keywords] of Object.entries(SECTION_KW)) {
    let score = 0;
    for (const kw of keywords) {
      if (lower.includes(kw)) score += kw.split(' ').length * 3;
    }
    if (score > bestScore) { bestScore = score; best = sec; }
  }
  return bestScore >= 3 ? best : null;
}

// ─────────────────────────────────────────────────────────────────
// INTENT PATTERNS
// ─────────────────────────────────────────────────────────────────
const INTENT_MAP = {
  SET_ROLE:    ["i am a ","i'm a ","i am the ","i'm the ","my role is ","i work as a ","i work as ","im a ","im the ","switch to ","change role to ","change my role to ","set role to ","i am now a ","call me ","treat me as"],
  GREETING:    ['hi','hello','hey','good morning','good afternoon','good evening','salam','مرحبا','السلام'],
  PROGRESS:    ['my progress','how am i doing','how many steps','completion rate','done so far','my status','how far am i','overall progress','my stats','completion percentage'],
  WHATS_NEXT:  ["what's next","what is next",'next step','what should i do','guide me','where do i go','what do i do now','next action','where do i start','what do i work on','help me start','get started','begin'],
  BLOCKED:     ['i am blocked','i\'m blocked','i have a blocker','stuck on','i am stuck','obstacle','show blocked','my blockers','unblock','what is blocking me','what is blocking'],
  SHOW_STEPS:  ['show steps','all steps','list steps','show checklist','what steps','steps for this','give me the steps','show me the steps','all checklist'],
  RULES:       ['what are the rules','key rules','important rules','mandatory rules','must i do','show rules','rules for','what must i'],
  MY_ROLE:     ['my role','my sections','what do i own','my responsibilities','what am i responsible','my duties','my tasks','what sections'],
  EXPLAIN:     ['explain','tell me about','what is this','describe this','how does this','clarify','what does this section','about this section'],
  THANKS:      ['thanks','thank you','great thanks','ok thanks','شكرا','perfect','got it thanks','understood thanks'],
  HELP:        ['help','how do i use','what can you do','show commands','what can i ask','options','how does this work','what do you know'],
};

function classifyIntent(input) {
  const lower = input.toLowerCase().trim();
  const padded = ' ' + lower + ' ';

  // SET_ROLE — check first with role validation
  for (const prefix of INTENT_MAP.SET_ROLE) {
    if (lower.startsWith(prefix.trim()) || padded.includes(prefix)) {
      const role = detectRole(input);
      if (role) return { intent: 'SET_ROLE', role };
    }
  }
  // Also catch standalone "i'm devops" / "i'm a qa" patterns
  const implicitRole = detectRole(input);
  if (implicitRole && lower.length < 40) {
    const hasRoleIndicator = ['i am','i\'m','im ','my role','i work','i\'m a','switch','change'].some(p => lower.includes(p));
    if (hasRoleIndicator) return { intent: 'SET_ROLE', role: implicitRole };
  }

  // Score remaining intents
  const scores = {};
  for (const [intent, patterns] of Object.entries(INTENT_MAP)) {
    if (intent === 'SET_ROLE') continue;
    let score = 0;
    for (const p of patterns) {
      if (padded.includes(' ' + p + ' ') || lower === p.trim()) score += p.split(' ').length * 3;
      else if (lower.includes(p.trim())) score += p.split(' ').length * 2;
    }
    if (score > 0) scores[intent] = score;
  }

  const sorted = Object.entries(scores).sort((a, b) => b[1] - a[1]);
  return sorted.length ? { intent: sorted[0][0] } : { intent: 'UNKNOWN' };
}

// ─────────────────────────────────────────────────────────────────
// CONTEXT BUILDER
// ─────────────────────────────────────────────────────────────────
export function buildContext(state, currentSection) {
  const role     = state.role ? ROLES[state.role] : null;
  const overall  = state.role ? getOverallProgress(state.progress, state.role) : null;
  const secData  = currentSection ? SECTIONS[currentSection] : null;
  const secProg  = currentSection ? getSectionProgress(state.progress, currentSection) : null;
  const curPhase = currentSection ? PHASES.find(p => p.keys.includes(currentSection)) : null;

  let nextIncomplete = null;
  if (role) {
    for (const sk of role.sections) {
      if (getSectionProgress(state.progress, sk).pct < 100) { nextIncomplete = sk; break; }
    }
  }

  return { role, roleKey: state.role, overall, secData, secProg, currentSection, curPhase, nextIncomplete, progress: state.progress };
}

// ─────────────────────────────────────────────────────────────────
// MAIN MATCH FUNCTION
// ─────────────────────────────────────────────────────────────────
export function matchIntent(input, ctx, onSetRole) {
  const { intent, role } = classifyIntent(input);

  // ── Role switch ──
  if (intent === 'SET_ROLE' && role) {
    if (onSetRole) onSetRole(role);
    const r = ROLES[role];
    const sameRole = ctx.roleKey === role;
    return {
      text: sameRole
        ? `You're already set as **${r.label}**. I have full context for your **${r.sections.length} sections**. What do you need?`
        : `Done! I've switched your role to **${r.label}**.\n\nYou now have **${r.sections.length} sections** to work through across the SDLC. Your previous progress is preserved.\n\nWhat would you like to do first?`,
      suggestions: ["What's my next step?", 'Show my sections', 'My responsibilities'],
    };
  }

  // ── Section-specific query ──
  const sec = detectSection(input);
  if (sec && !['WHATS_NEXT','PROGRESS','MY_ROLE','GREETING','THANKS','SET_ROLE','HELP'].includes(intent)) {
    return sectionResponse(sec, ctx);
  }

  switch (intent) {
    case 'GREETING':   return greetingResponse(ctx);
    case 'PROGRESS':   return progressResponse(ctx);
    case 'WHATS_NEXT': return whatsNextResponse(ctx);
    case 'BLOCKED':    return blockedResponse(ctx);
    case 'SHOW_STEPS': return checklistResponse(ctx);
    case 'RULES':      return rulesResponse(ctx, sec);
    case 'MY_ROLE':    return myRoleResponse(ctx);
    case 'EXPLAIN':    return sec ? sectionResponse(sec, ctx) : explainCurrentResponse(ctx);
    case 'THANKS':     return thanksResponse();
    case 'HELP':       return helpResponse(ctx);
    default:           return fallbackResponse(ctx, input);
  }
}

// ─────────────────────────────────────────────────────────────────
// RESPONSES
// ─────────────────────────────────────────────────────────────────

function greetingResponse(ctx) {
  const h = new Date().getHours();
  const g = h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
  if (!ctx.role) {
    return {
      text: `${g}! I'm your SWISO TECH Process Guide assistant.\n\nI can help you navigate the handbook, understand rules, find your next action, and track your progress.\n\nTo get started, just tell me your role — for example:\n*"I'm a Project Manager"* or *"I'm a Developer"*`,
      suggestions: ["I'm a Project Manager","I'm a Developer","I'm a Tester","I'm a DevOps Engineer"],
    };
  }
  const o = ctx.overall;
  return {
    text: `${g}! You're set as **${ctx.role.label}**.\n\n**Status:** ${o.pct}% complete — ${o.done}/${o.total} steps done${o.blocked > 0 ? `, ${o.blocked} blocked ⚠️` : ''}.\n\nWhat can I help you with?`,
    suggestions: ["What's my next step?","My progress breakdown",ctx.currentSection ? 'Explain this section' : 'Show my sections'],
    actions: ctx.nextIncomplete && o.pct < 100
      ? [{ label: `Resume: ${ctx.nextIncomplete} — ${SECTIONS[ctx.nextIncomplete]?.title}`, section: ctx.nextIncomplete }]
      : [],
  };
}

function progressResponse(ctx) {
  if (!ctx.role) return noRoleResponse();
  const o = ctx.overall;
  const mood =
    o.pct === 100 ? '🎉 All sections complete — outstanding!' :
    o.pct >= 75   ? 'Final stretch — keep going.' :
    o.pct >= 50   ? 'Past halfway — good momentum.' :
    o.pct >= 25   ? 'Getting started — keep the pace.' :
                    'Just beginning — your first steps are waiting.';

  let text = `**Progress — ${ctx.role.label}:**\n\n`;
  text += `• Overall: **${o.pct}%** (${o.done}/${o.total} steps)\n`;
  if (o.inProg > 0) text += `• In progress: **${o.inProg}** steps\n`;
  if (o.blocked > 0) text += `• ⚠️ Blocked: **${o.blocked}** steps — need attention\n`;
  text += `\n${mood}`;

  const phLines = [];
  for (const ph of PHASES) {
    const rel = ph.keys.filter(k => ctx.role.sections.includes(k));
    if (!rel.length) continue;
    let t = 0, d = 0;
    rel.forEach(k => { const sp = getSectionProgress(ctx.progress, k); t += sp.total; d += sp.done; });
    const pp = t ? Math.round(d / t * 100) : 0;
    if (pp < 100) phLines.push(`${ph.code}: ${pp}%`);
  }
  if (phLines.length) text += `\n\n**Incomplete phases:** ${phLines.join(' · ')}`;

  return {
    text,
    suggestions: ["What's next?","Show blocked steps","My sections overview"],
    actions: ctx.nextIncomplete
      ? [{ label: `Next: ${ctx.nextIncomplete} — ${SECTIONS[ctx.nextIncomplete]?.title}`, section: ctx.nextIncomplete }]
      : [],
  };
}

function whatsNextResponse(ctx) {
  if (!ctx.role) return noRoleResponse();
  const ni = ctx.nextIncomplete;
  if (!ni) {
    return {
      text: `**All sections complete for ${ctx.role.label}!** 🎉\n\nGreat work. Consider:\n• Running the project retrospective (7.3)\n• Reviewing lessons learned with your team\n• Checking with your PM for any new assignments`,
      suggestions: ['Open retrospective 7.3','My progress summary'],
    };
  }
  const sec = SECTIONS[ni];
  const sp  = getSectionProgress(ctx.progress, ni);
  const firstPending = sec.checklist.findIndex((_, i) => {
    const st = ctx.progress?.[ni]?.[i]?.status;
    return !st || st === 'Not Started';
  });

  let text = `**Next action: ${ni} — ${sec.title}**\n${sec.phase}\n\n`;
  if (sp.pct > 0) text += `You're **${sp.pct}%** through this section (${sp.done}/${sp.total} done).\n\n`;
  if (firstPending >= 0) {
    const [step, owner] = sec.checklist[firstPending];
    text += `**Immediate next step:**\n"${step}"\n\n*Responsible: ${owner}*`;
  }
  return {
    text,
    suggestions: ['Show all steps in this section','Rules for this section','Skip to next section'],
    actions: [{ label: `Open ${ni} — ${sec.title}`, section: ni }],
  };
}

function blockedResponse(ctx) {
  if (!ctx.role) return noRoleResponse();
  const o = ctx.overall;
  if (o.blocked === 0) {
    return {
      text: `✅ No blocked steps right now.\n\nIf you hit a blocker:\n• Log it in the tracking system immediately\n• Notify your PM the same working day\n• Critical/High bugs: escalate to PM + COO immediately\n• Deployment blockers: DevOps escalates to COO in writing`,
      suggestions: ["What's my next step?",'Deployment rules'],
    };
  }
  const items = [];
  for (const sk of ctx.role.sections) {
    const pd = ctx.progress[sk] || {};
    Object.entries(pd).forEach(([i, d]) => {
      if (d.status === 'Blocked') {
        items.push({ sec: sk, step: SECTIONS[sk]?.checklist[i]?.[0], owner: SECTIONS[sk]?.checklist[i]?.[1] });
      }
    });
  }
  let text = `⚠️ **${o.blocked} blocked step${o.blocked > 1 ? 's' : ''}** need attention:\n\n`;
  items.slice(0, 5).forEach(b => { text += `• **${b.sec}:** "${b.step}" *(${b.owner})*\n`; });
  if (items.length > 5) text += `• ...and ${items.length - 5} more\n`;
  text += `\n**Action required:** Raise all blockers to your PM immediately. Any blocker older than 1 working day must be formally escalated.`;
  return { text, suggestions: ['How do I escalate?',"What's my next unblocked step?"] };
}

function checklistResponse(ctx) {
  if (!ctx.currentSection) return whatsNextResponse(ctx);
  const sec = SECTIONS[ctx.currentSection];
  const sp  = ctx.secProg;
  const statusOf = (i) => ctx.progress?.[ctx.currentSection]?.[i]?.status || 'Not Started';
  let text = `**${ctx.currentSection} — ${sec.title}** (${sp.pct}% complete)\n\n`;
  sec.checklist.forEach(([step, owner], i) => {
    const s = statusOf(i);
    const ic = s === 'Done' ? '✓' : s === 'In Progress' ? '◑' : s === 'Blocked' ? '✗' : '○';
    text += `${ic} ${i + 1}. ${step} *(${owner})*\n`;
  });
  return {
    text,
    suggestions: ["What's next after this?",'Show rules for this section'],
    actions: [{ label: `Open ${ctx.currentSection} checklist`, section: ctx.currentSection }],
  };
}

function rulesResponse(ctx, detectedSection) {
  if (detectedSection) return sectionResponse(detectedSection, ctx);
  if (ctx.currentSection) return sectionResponse(ctx.currentSection, ctx);
  return {
    text: `**Core mandatory rules — SWISO TECH SDLC:**\n\n**Initiation:** No work starts without a signed scope document.\n\n**Requirements:** No design begins without signed requirements. Post-sign-off changes go through a formal Change Request.\n\n**Development:** Always pull the latest branch before coding. All changes via Pull Request. No direct pushes.\n\n**UAT:** PM written sign-off on 3 deliverables before UAT date is communicated.\n\n**Deployment:** 3 written approvals — FC, Tester, PM. Staging mandatory. Rollback plan required.\n\n**Documents:** Start at v0.1. Store in central system immediately. Never delete old versions.\n\n**Communication:** Client commitments in writing. PM must approve all outgoing client communication.`,
    suggestions: ['Git rules','Deployment rules','UAT rules','Document versioning rules'],
  };
}

function myRoleResponse(ctx) {
  if (!ctx.role) return noRoleResponse();
  const r = ctx.role;
  let text = `**Your role: ${r.label}**\n\nYou own **${r.sections.length} sections** across the SDLC:\n\n`;
  for (const ph of PHASES) {
    const rel = ph.keys.filter(k => r.sections.includes(k));
    if (!rel.length) continue;
    text += `**${ph.code} — ${ph.title}:** ${rel.join(', ')}\n`;
  }
  const o = ctx.overall;
  text += `\n**Progress: ${o.pct}%** — ${o.done}/${o.total} steps done.`;
  if (o.blocked > 0) text += ` ${o.blocked} blocked ⚠️`;
  return {
    text,
    suggestions: ["What's next?","My blocked steps","My progress details"],
    actions: ctx.nextIncomplete
      ? [{ label: `Next: ${ctx.nextIncomplete} — ${SECTIONS[ctx.nextIncomplete]?.title}`, section: ctx.nextIncomplete }]
      : [],
  };
}

function explainCurrentResponse(ctx) {
  if (ctx.currentSection) return sectionResponse(ctx.currentSection, ctx);
  return helpResponse(ctx);
}

const SECTION_KEYRULES = {
  '0.1': 'Internal kickoff must happen before the client kickoff. No work starts before scope is signed.',
  '0.3': 'Project must be fully set up in tracking system before any task is created.',
  '1.2': 'No design or development starts before requirements are signed off by the client.',
  '1.3': 'No change to signed requirements without a formal CR approved in writing. No work on unapproved changes.',
  '2.2': 'Development cannot begin without a client-signed design document. This gate cannot be bypassed.',
  '3.1': 'Verbal or messaging-app assignments are invalid. All tasks must exist in the tracking system.',
  '3.2': 'Pull the latest testing branch before writing a single line of code. No direct pushes to testing or master.',
  '3.3': 'Self-reviewed code is not accepted. Every task needs peer reviewer approval via Pull Request.',
  '4.1': 'No Critical or High bugs can remain open before proceeding to UAT.',
  '4.2': 'Bugs are never closed verbally. All status changes must be in the tracking system.',
  '5.1': 'No UAT date communicated to client without PM written sign-off on all 3 deliverables.',
  '5.2': 'FC must facilitate every UAT session. UAT is never left to the client to run alone.',
  '5.3': 'No production deployment without a signed UAT Acceptance Document.',
  '6.1': '3 written approvals required — FC, Tester, PM — before any deployment. Staging validation cannot be skipped.',
  '6.2': 'Pre-deployment notification to client at least 2 working days before go-live.',
  '7.1': 'No invoice can be issued without a signed Milestone Completion Certificate.',
  '7.3': 'Retrospective must happen within 5 working days of project closure.',
  'X.1': 'Every request must be logged as a task within 24 hours. No commitment without a tracking system entry.',
  'X.2': 'All client commitments must be in writing. PM must be aware of all outgoing client communication.',
  'X.3': 'Store documents in central system immediately — not after finalization. Never delete old versions.',
};

function sectionResponse(key, ctx) {
  const sec = SECTIONS[key];
  if (!sec) return fallbackResponse(ctx, key);

  const inRole = ctx.role ? ctx.role.sections.includes(key) : true;
  const sp     = ctx.role ? getSectionProgress(ctx.progress, key) : null;

  let text = `**${key} — ${sec.title}**\n${sec.phase}\n\n`;
  if (sp) text += `Your progress: **${sp.pct}%** (${sp.done}/${sp.total} steps)\n\n`;
  if (SECTION_KEYRULES[key]) text += `**Key rule:** ${SECTION_KEYRULES[key]}\n\n`;

  text += `**Checklist (${sec.checklist.length} steps):**\n`;
  sec.checklist.slice(0, 4).forEach(([step, owner]) => { text += `• ${step} *(${owner})*\n`; });
  if (sec.checklist.length > 4) text += `• ...+${sec.checklist.length - 4} more steps`;

  if (!inRole && ctx.role) {
    text += `\n\n*Note: ${key} is not in your ${ctx.role.label} role — coordinate with the team member who owns it.*`;
  }

  return {
    text,
    suggestions: ['Show all steps',"What's next after this?",'Other rules I should know'],
    actions: inRole ? [{ label: `Open ${key} — ${sec.title}`, section: key }] : [],
  };
}

function thanksResponse() {
  const msgs = [
    "You're welcome! Ask me anything else about the processes.",
    "Happy to help! Keep going — great progress.",
    "Anytime! If you get stuck on any step or rule, just ask.",
    "Great! What else can I help you with?",
  ];
  return {
    text: msgs[Math.floor(Math.random() * msgs.length)],
    suggestions: ["What's my next step?",'My progress','Show blocked steps'],
  };
}

function helpResponse(ctx) {
  return {
    text: `**What I can help you with:**\n\n• **"What's next?"** — your immediate next action\n• **"My progress"** — completion stats across all phases\n• **"I'm blocked"** — list all your blocked steps\n• **"Git rules"** or **"Deployment rules"** — section-specific guidance\n• **"3.2"** or **"X.3"** — jump to any section by code\n• **"Explain this section"** — details on current section\n• **"I'm a Developer"** or **"I'm a DevOps"** — switch your role\n• **"My responsibilities"** — all your sections at a glance`,
    suggestions: ["What's next?",'My progress','My role overview','Show blocked steps'],
  };
}

function noRoleResponse() {
  return {
    text: `I need to know your role first so I can give you personalised guidance.\n\nJust tell me — for example:\n• *"I'm a Project Manager"*\n• *"I'm a Developer"*\n• *"I'm a DevOps Engineer"*\n• *"I'm a Tester"*`,
    suggestions: ["I'm a Project Manager","I'm a Developer","I'm a DevOps Engineer","I'm a Tester"],
  };
}

function fallbackResponse(ctx, input) {
  const lower = input.toLowerCase();
  const matched = Object.entries(SECTIONS).find(([k, s]) =>
    s.title.toLowerCase().split(' ').some(w => w.length > 4 && lower.includes(w))
  );
  if (matched) {
    return {
      text: `Did you mean **${matched[0]} — ${matched[1].title}**?`,
      suggestions: [`Tell me about ${matched[0]}`,"What's next?",'Help'],
      actions: ctx.role?.sections.includes(matched[0]) ? [{ label: `Open ${matched[0]}`, section: matched[0] }] : [],
    };
  }
  return {
    text: `I didn't quite catch that. Try:\n\n• **"What's next?"** — your next action\n• **"My progress"** — completion stats\n• **"3.2"** — any section by code\n• **"I'm a Developer"** — switch role\n• **"Help"** — see all commands`,
    suggestions: ["What's next?",'My progress','I need help',ctx.role ? 'My sections' : "I'm a Project Manager"],
  };
}

// ─────────────────────────────────────────────────────────────────
// QUICK SUGGESTIONS
// ─────────────────────────────────────────────────────────────────
export function getQuickSuggestions(ctx) {
  if (!ctx.role) return ["I'm a Project Manager","I'm a Developer","I'm a DevOps Engineer","I'm a Tester"];
  if (ctx.currentSection) return ['Explain this section','Show all steps',"What's next?",'I\'m blocked'];
  const o = ctx.overall;
  if (o.pct === 0)   return ['Where do I start?','My responsibilities','What sections do I own?'];
  if (o.blocked > 0) return ['Show my blocked steps',"What's next?",'My progress','How do I escalate?'];
  if (o.pct === 100) return ['All done — what now?','Run retrospective 7.3','Closure steps'];
  return ["What's my next action?",'My progress','Show blocked steps','My sections'];
}
